import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { Logo } from '@/components/generals/logo'

import { siteConfig } from '@/constants/site'
import { NAV_LINKS, SHARE_LINK } from '@/constants/content'

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='bg-forest-deep text-linen'>
            <div className='mx-auto grid max-w-6xl gap-12 px-5 pt-16 pb-12 md:grid-cols-[1.6fr_1fr_1.2fr] md:px-8'>
                <div>
                    <Link href='/' className='inline-block' aria-label={`${siteConfig.name}, home`}>
                        <Logo light />
                    </Link>

                    <p className='mt-5 max-w-sm text-[15px] leading-relaxed text-linen/75'>
                        A home for the names, places and stories of the family of Marga Theron, kept for the generations still to come.
                    </p>
                </div>

                <nav aria-label="Footer">
                    <h2 className='font-heading text-lg font-semibold text-linen'>
                        Explore
                    </h2>

                    <ul className='mt-4 space-y-2.5'>
                        {[...NAV_LINKS, SHARE_LINK].map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='text-[15px] text-linen/75 underline-offset-4 hover:text-linen hover:underline'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div>
                    <h2 className='font-heading text-lg font-semibold text-linen'>
                        Help the tree grow
                    </h2>

                    <p className='mt-4 max-w-xs text-[15px] leading-relaxed text-linen/75'>
                        Know a name, a date or a story we are missing? Every memory you share helps complete the picture.
                    </p>

                    <Link
                        href={SHARE_LINK.href}
                        className='group mt-5 inline-flex items-center gap-2 font-semibold text-linen underline decoration-gold decoration-2 underline-offset-[6px] hover:decoration-linen'
                    >
                        Add your story
                        <ArrowRight className='size-4 transition-transform duration-200 group-hover:translate-x-1' aria-hidden='true' />
                    </Link>
                </div>
            </div>

            <div className='border-t border-linen/15'>
                <div className='mx-auto flex max-w-6xl flex-col gap-2 px-5 py-6 text-sm text-linen/65 md:flex-row md:items-center md:justify-between md:px-8'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}
                    </p>

                    <p>
                        Made with love, for every branch of the family.
                    </p>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
